import type { ComponentType, SVGProps } from 'react';
import {
  ArrowUpTrayIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { hasPermission } from '@/config/accessControlCatalog';

export type DocumentHubSectionId =
  | 'catalog'
  | 'acquisition'
  | 'management'
  | 'links'
  | 'configuration';

export type DocumentHubSection = {
  id: DocumentHubSectionId;
  label: string;
  homeLabel?: string;
  description: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  required: string[];
  oneOf?: string[];
};

const consoleApp = 'app:documenti.console:view';
const documentUpdate = 'feature:documenti.console.documents:update';
const documentManage = 'feature:documenti.console.documents:manage';

export const DOCUMENT_HUB_SECTIONS: DocumentHubSection[] = [
  {
    id: 'catalog',
    label: 'Catalogo',
    homeLabel: 'Documenti',
    description: 'Consulta il catalogo, filtra i documenti e apri le versioni disponibili.',
    href: '/documents/catalog',
    icon: BookOpenIcon,
    required: ['hub:documenti:view'],
  },
  {
    id: 'acquisition',
    label: 'Acquisizione',
    description: 'Carica documenti e completa la revisione delle proposte.',
    href: '/documents/acquisition',
    icon: ArrowUpTrayIcon,
    required: [consoleApp, documentUpdate],
  },
  {
    id: 'management',
    label: 'Gestione documenti',
    description: 'Modifica metadati, versioni, codici e stato dei documenti.',
    href: '/documents/manage',
    icon: DocumentTextIcon,
    required: [consoleApp],
    oneOf: [documentUpdate, documentManage],
  },
  {
    id: 'links',
    label: 'Collegamenti',
    description: 'Rivedi collegamenti proposti e riferimenti in attesa.',
    href: '/documents/links',
    icon: LinkIcon,
    required: [consoleApp],
    oneOf: [documentUpdate, documentManage],
  },
  {
    id: 'configuration',
    label: 'Configurazione',
    description: 'Gestisci sistemi, tassonomie, tipi documento e alias.',
    href: '/documents/configuration',
    icon: Cog6ToothIcon,
    required: [consoleApp],
    oneOf: [
      'feature:documenti.console.taxonomies:view',
      'feature:documenti.console.document-types:view',
    ],
  },
];

export function getVisibleDocumentHubSections(permissions: string[]): DocumentHubSection[] {
  return DOCUMENT_HUB_SECTIONS.filter((section) => (
    section.required.every((permission) => hasPermission(permissions, permission))
    && (!section.oneOf || section.oneOf.some((permission) => hasPermission(permissions, permission)))
  ));
}

export function isDocumentHubSectionCurrent(
  section: DocumentHubSectionId,
  currentSection: string,
): boolean {
  if (section === 'catalog') {
    return currentSection === 'documents/catalog'
      || /^documents\/[0-9a-f-]{36}$/i.test(currentSection);
  }
  if (section === 'acquisition') {
    return currentSection === 'documents/acquisition'
      || /^documents\/acquisition\/[0-9a-f-]{36}$/i.test(currentSection);
  }
  if (section === 'management') return currentSection === 'documents/manage';
  if (section === 'links') return currentSection === 'documents/links';
  return currentSection === 'documents/configuration'
    || currentSection.startsWith('documents/configuration/');
}
